import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Send } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Products with a real deliverable on disk (see backend DELIVERABLES map).
const DELIVERABLES = [
  { slug: "ai-writing-kit", name: "AI Writing Kit (zip · 6 files)" },
  { slug: "summary-prompts-pack", name: "Summary Prompts Pack (zip · 6 files)" },
  { slug: "content-workflow-system", name: "Content Workflow System (zip · 6 files)" },
  { slug: "notion-planner-kit", name: "Notion Planner Kit (zip · 9 files)" },
  { slug: "vaymilo-starter-bundle", name: "Starter Bundle (zip · 22 files)" },
];

export const IssueDownloadCard = () => {
  const { getToken } = useAuth();
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState(DELIVERABLES[0].slug);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/downloads`,
        { email: email.trim(), slug },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setResult(data);
      if (data.email_status === "sent") {
        toast.success(`Download link emailed to ${email.trim()}.`);
      } else {
        toast.warning(`Token issued, but email ${data.email_status}. Copy the link manually.`);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}${result.url}`);
    toast.success("Download link copied.");
  };

  return (
    <div
      data-testid="issue-download-card"
      className="bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8"
    >
      <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
        Fulfillment
      </div>
      <h3 className="mt-4 font-sora text-[20px] md:text-[22px] font-semibold text-[#111111]">
        Issue a download link
      </h3>
      <p className="mt-2 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
        Creates a one-time download token and emails it to the buyer. Use for
        manual fulfillment, refund recoveries, or support requests.
      </p>

      <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
        <input
          required
          type="email"
          data-testid="dl-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="buyer@email.com"
          className="flex-1 bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm font-manrope focus:outline-none focus:border-[#2563EB]"
        />
        <select
          data-testid="dl-slug-select"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm font-manrope focus:outline-none focus:border-[#2563EB]"
        >
          {DELIVERABLES.map((d) => (
            <option key={d.slug} value={d.slug}>
              {d.name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={busy}
          data-testid="dl-issue-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#111111] hover:bg-[#2563EB] disabled:opacity-60 text-white font-manrope font-medium text-sm px-5 py-2.5 rounded-lg transition-colors"
        >
          <Send size={14} />
          {busy ? "Issuing…" : "Issue & email"}
        </button>
      </form>

      {result && (
        <div
          data-testid="dl-result"
          className="mt-5 flex items-center gap-3 bg-[#F7F7F4] border border-[#E7E7E2] rounded-lg px-4 py-3"
        >
          <code className="flex-1 font-mono text-[12px] text-[#111111]/75 truncate">
            {process.env.REACT_APP_BACKEND_URL}
            {result.url}
          </code>
          <button
            type="button"
            onClick={copyLink}
            data-testid="dl-copy-btn"
            className="text-[#111111]/55 hover:text-[#2563EB]"
            title="Copy link"
          >
            <Copy size={15} />
          </button>
        </div>
      )}
    </div>
  );
};
